/**
 * Draining the to-device mailbox.
 *
 * This is how a room key reaches the browser. A sender Olm-encrypts an
 * `m.room_key` to each recipient device; the server relays it blind; we
 * decrypt it with an Olm session and import the Megolm session inside.
 *
 * ## Acknowledge only after importing
 *
 * The fetch endpoint deliberately does not mark anything delivered - the
 * client acks separately, once the key is actually in the store. The comment
 * on the server explains why in detail: a room key is the least recoverable
 * payload in the system, and a device that fetched-then-crashed under
 * at-most-once delivery lost the key permanently, making every message under
 * that session undecryptable forever. Re-importing is harmless, so this
 * errs toward doing it twice rather than zero times.
 *
 * ## What else arrives here
 *
 * The mailbox is the protocol's control channel, not just a key drop:
 *
 *   - `m.room_key_request` - a device (usually the phone, sometimes a peer)
 *     could not read something THIS browser encrypted and is asking for the
 *     session. Answered for sessions we created, to members of the room.
 *   - the phone's `to_device` nudge on the user socket is what triggers a
 *     drain between page loads; see `lib/userSocket.ts`.
 */
import { loadCrypto } from '@nsibi/e2ee-wasm';

import { api } from '../lib/api';
import { getDeviceId } from '../lib/device';
import {
  loadAccount,
  loadMegolmSession,
  loadOlmSession,
  saveAccount,
  saveMegolmSession,
  saveOlmSession,
} from './store';

type ToDeviceRow = {
  id: number;
  sender_user_id: number;
  sender_device_id: string;
  message_type: string;
  ciphertext: string;
  created_at: string;
};

/** The Olm-encrypted envelope the sender put in `ciphertext`. */
type OlmEnvelope = {
  type: number;
  body: string;
  /** Sender's curve25519 identity key - which Olm session this belongs to. */
  sender_key: string;
};

const ROOM_KEY = 'm.room_key';
/** A key being passed on rather than announced. Different wire format. */
const FORWARDED_ROOM_KEY = 'm.forwarded_room_key';
const ROOM_KEY_REQUEST = 'm.room_key_request';
const HISTORY_PAGE = 'm.nsibi.history_page';

type RoomKeyPayload = {
  type?: string;
  content?: {
    room_id?: string;
    session_id?: string;
    session_key?: string;
    sender_key?: string;
  };
};

export type RoomKeyRequestContent = {
  action?: string;
  room_id?: string | null;
  session_id?: string;
  sender_key?: string;
  requesting_device_id?: string;
};

/** Who to tell when a key request arrives. Wired by `keyRequests.ts`, which
 *  owns the answer; this module only recognises the shape. */
export type KeyRequestHandler = (
  content: RoomKeyRequestContent,
  from: { userId: number; deviceId: string },
) => Promise<void>;
let keyRequestHandler: KeyRequestHandler | null = null;
export function setKeyRequestHandler(fn: KeyRequestHandler | null): void {
  keyRequestHandler = fn;
}

/** A page of history pushed by the phone in reply to a request. Routed to
 *  whoever registered here (lib/historyPaging.ts). */
export type HistoryPage = {
  channel_id: string;
  before_id: string | null;
  messages: unknown[];
  has_more: boolean;
};
let historyPageHandler: ((page: HistoryPage) => void) | null = null;
export function setHistoryPageHandler(fn: ((page: HistoryPage) => void) | null): void {
  historyPageHandler = fn;
}

/* ── listeners ───────────────────────────────────────────────────────── */

type KeysImportedListener = (count: number) => void;
const listeners = new Set<KeysImportedListener>();

/**
 * Be told when a drain imports at least one Megolm session.
 *
 * An open conversation uses this to retry the bubbles it could not read:
 * the key for them may have just landed in response to a request, or a
 * nudge from the user socket, neither of which re-renders anything on its
 * own.
 */
export function onKeysImported(fn: KeysImportedListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notifyKeysImported(count: number): void {
  for (const fn of listeners) {
    try {
      fn(count);
    } catch {
      /* a listener must never break a drain */
    }
  }
}

/* ── draining ────────────────────────────────────────────────────────── */

let inFlight: Promise<number> | null = null;

/**
 * Fetch, decrypt and import everything waiting for this device.
 * Returns how many Megolm sessions were newly imported.
 *
 * Concurrent callers share one run: the nudge, a conversation opening and
 * the initial bootstrap can all ask at once, and three drains of one mailbox
 * would ack each other's rows.
 */
export function drainToDevice(): Promise<number> {
  if (inFlight) return inFlight;
  inFlight = doDrain().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doDrain(): Promise<number> {
  const deviceId = getDeviceId();
  const res = await api<{ messages: ToDeviceRow[] }>(
    `/v1/toDevice/?device_id=${encodeURIComponent(deviceId)}`,
  );
  const rows = res.messages ?? [];
  if (rows.length === 0) return 0;

  const imported: number[] = [];
  let sessions = 0;

  for (const row of rows) {
    try {
      const plaintext = await openOlm(row);
      if (plaintext === null) {
        // Unopenable for good - no session and not a pre-key message. It
        // would fail identically on every drain, so retire it rather than
        // re-reading it forever.
        imported.push(row.id);
        continue;
      }
      if (await handlePayload(plaintext, row)) sessions += 1;
      imported.push(row.id);
    } catch (err) {
      // One undecryptable envelope must not strand the rest of the mailbox.
      // Leaving it unacked means it comes back next drain, which is the
      // correct outcome for a transient failure and harmless for a
      // permanent one.
      console.warn('[nsibi] to-device message could not be opened', row.id, err);
    }
  }

  if (imported.length) {
    await api('/v1/toDevice/ack/', { method: 'POST', body: { ids: imported } });
  }
  if (sessions > 0) notifyKeysImported(sessions);
  return sessions;
}

/**
 * Decrypt one envelope, creating the inbound Olm session if this is the
 * first message from that device.
 *
 * A pre-key message (type 0) can ALSO arrive when we already hold a session
 * with that device: the phone re-provisioned, or both sides started a
 * session toward each other before either had received anything. The
 * existing session cannot open it, and treating that as a failure left the
 * row unacked and re-failing on every drain. So: try what we have, and on a
 * pre-key message fall back to starting the session it describes.
 */
async function openOlm(row: ToDeviceRow): Promise<string | null> {
  const core = await loadCrypto();
  const envelope = JSON.parse(row.ciphertext) as OlmEnvelope;
  const senderKey = envelope.sender_key;
  if (!senderKey) return null;

  const existing = await loadOlmSession(senderKey);
  if (existing) {
    try {
      const out = core.sessionDecrypt(existing, envelope.type, envelope.body);
      await saveOlmSession(senderKey, out.session_pickle);
      return out.plaintext;
    } catch (err) {
      if (envelope.type !== 0) throw err;
      // Fall through: a fresh pre-key for a new session.
    }
  }

  // No usable session. Only a pre-key message (type 0) can start one; a
  // normal message with no session is unrecoverable and there is nothing
  // useful to do but skip it.
  if (envelope.type !== 0) return null;

  const account = await loadAccount();
  // Not "unrecoverable": the store is not ready. Throw so the row stays
  // unacked and is retried once it is, rather than being retired.
  if (!account) throw new Error('No Olm account yet.');

  const inbound = core.createInboundSession(account, senderKey, envelope.type, envelope.body);
  // Creating an inbound session consumes a one-time key, so the account
  // pickle changes and must be persisted or the key is spent twice.
  await saveAccount(inbound.account_pickle);
  await saveOlmSession(senderKey, inbound.session_pickle);
  return inbound.plaintext;
}

/** Dispatch on the inner payload type. Returns true when a Megolm session
 *  was newly imported. */
async function handlePayload(plaintext: string, row: ToDeviceRow): Promise<boolean> {
  let payload: RoomKeyPayload & { content?: RoomKeyRequestContent };
  try {
    payload = JSON.parse(plaintext) as typeof payload;
  } catch {
    return false;
  }
  switch (payload.type) {
    case ROOM_KEY:
    case FORWARDED_ROOM_KEY:
      return importRoomKey(payload as RoomKeyPayload);
    case ROOM_KEY_REQUEST:
      if (keyRequestHandler) {
        await keyRequestHandler(payload.content ?? {}, {
          userId: row.sender_user_id,
          deviceId: row.sender_device_id,
        });
      }
      return false;
    case HISTORY_PAGE:
      if (historyPageHandler && payload.content) {
        historyPageHandler(payload.content as unknown as HistoryPage);
      }
      return false;
    default:
      // Unknown control type - ignored for forward-compat.
      return false;
  }
}

/**
 * Import the Megolm session inside a room-key payload.
 *
 * The two types nominally carry DIFFERENT key formats:
 *
 *   m.room_key            SessionKey          -> createInboundGroupSession
 *   m.forwarded_room_key  ExportedSessionKey  -> importInboundGroupSession
 *
 * In practice the type is not a reliable guide to the bytes. The phone's
 * re-share paths - sharing a running session with a device that joined, and
 * answering a key request - export their inbound copy and have historically
 * sent that EXPORTED key under `m.room_key`. Dispatching strictly on type
 * fed those to the fresh-key importer, which failed with "session key had a
 * invalid version, expected 2, got 1"; the row was never acked, came back on
 * every drain, and every message under that session stayed locked. That is
 * exactly the key a freshly linked browser receives first, because the phone
 * already has a running session for every conversation.
 *
 * So: try the importer the type suggests, then the other one. A key that
 * fails both is genuinely bad and is reported; a key that passes either is
 * the same session whichever door it came through.
 */
async function importRoomKey(payload: RoomKeyPayload): Promise<boolean> {
  const core = await loadCrypto();
  const key = payload.content?.session_key;
  if (!key) return false;

  const fresh = () => core.createInboundGroupSession(key);
  const exported = () => core.importInboundGroupSession(key);
  const [first, second] = payload.type === FORWARDED_ROOM_KEY ? [exported, fresh] : [fresh, exported];

  let session;
  try {
    session = first();
  } catch (firstErr) {
    try {
      session = second();
    } catch (secondErr) {
      console.warn('[nsibi] room key rejected in both formats', firstErr, secondErr);
      throw secondErr;
    }
  }

  // Never let a re-share rewind a session we already hold: an older copy of
  // the same session decrypts less, not more, and accepting it is a known
  // key-sharing attack. First import wins.
  if (await loadMegolmSession(session.session_id)) return false;

  await saveMegolmSession(session.session_id, session.session_pickle);
  return true;
}
